// FILE: tcms-frontend/src/pages/AdminReportLllUsage.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { BASE_URL, security_key as SECURITY_KEY } from "../global";

// Use the same API path shape as other reports
const API_URL = `${BASE_URL}API/api_index.php`;

// --- helper: get the most useful error message from any backend/axios shape ---
function extractApiError(resOrErr, fallback = "Request failed") {
    try {
        if (resOrErr?.response?.data) {
            const d = resOrErr.response.data;
            return d?.message || d?.data?.error || (typeof d === "string" ? d : null) || fallback;
        }
        if (typeof resOrErr === "string") return resOrErr;
        return fallback;
    } catch (_) {
        return fallback;
    }
}

function formatMonthLabel(yyyyDashMm) {
    if (!yyyyDashMm) return "-";
    const [y, m] = yyyyDashMm.split("-");
    const date = new Date(Number(y), Number(m) - 1, 1);
    return date.toLocaleString(undefined, { month: "long", year: "numeric" });
}

function downloadCSV(filename, rows) {
    const escapeCSV = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? '"' + s.replaceAll('"', '""') + '"' : s;
    };
    const csv = rows.map((r) => r.map(escapeCSV).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export default function AdminReportLllUsage() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]); // [{month, unique_students_main, unique_students_trial}]

    const [showDetails, setShowDetails] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsRows, setDetailsRows] = useState([]);
    const [detailsMonth, setDetailsMonth] = useState(null); // "YYYY-MM"
    const [which, setWhich] = useState("all"); // all | main | trial

    const totals = useMemo(() => {
        let main = 0,
            trial = 0;
        rows.forEach((r) => {
            main += Number(r.unique_students_main || 0);
            trial += Number(r.unique_students_trial || 0);
        });
        return { main, trial, all: main + trial };
    }, [rows]);

    const fetchCounts = useCallback(async () => {
        setLoading(true);
        try {
            const payload = { security_key: SECURITY_KEY, mode: "getLllMonthlyUniqueCounts" };
            const { data } = await axios.post(API_URL, payload);
            if (!data?.success) throw new Error(data?.message || "Failed to load");
            setRows(Array.isArray(data.data) ? data.data : []);
        } catch (err) {
            toast.error(extractApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // mark where we came from, for AdminReport.js tab persistence behavior
        sessionStorage.setItem("previousPageAdminPathname", "/admin/AdminReportLllUsage");
        fetchCounts();
    }, [fetchCounts]);

    const openDetails = useCallback(
        async (yyyyDashMm) => {
            setDetailsMonth(yyyyDashMm);
            setShowDetails(true);
            setDetailsLoading(true);
            try {
                const [year, monthStr] = yyyyDashMm.split("-");
                const payload = {
                    security_key: SECURITY_KEY,
                    mode: "getLllMonthStudentList",
                    year: Number(year),
                    month: Number(monthStr),
                    which,
                };
                const { data } = await axios.post(API_URL, payload);
                if (!data?.success) throw new Error(data?.message || "Failed to load details");
                setDetailsRows(Array.isArray(data.data) ? data.data : []);
            } catch (err) {
                toast.error(extractApiError(err));
                setDetailsRows([]);
            } finally {
                setDetailsLoading(false);
            }
        },
        [which]
    );

    const refreshDetails = useCallback(() => {
        if (detailsMonth) openDetails(detailsMonth);
    }, [detailsMonth, openDetails]);

    const onExportMonthly = () => {
        const header = ["Month", "Main Students", "Trial Students", "Total"];
        const body = rows.map((r) => [
            r.month,
            r.unique_students_main,
            r.unique_students_trial,
            Number(r.unique_students_main || 0) + Number(r.unique_students_trial || 0),
        ]);
        downloadCSV(`lll-usage-monthly.csv`, [header, ...body]);
    };

    const onExportDetails = () => {
        const header = [
            "Centre Key",
            "Centre Name",
            "Student Key",
            "First Name",
            "Surname",
            "First Session",
            "Classification",
            "Subscription Type @ Session",
        ];
        const body = detailsRows.map((r) => [
            r.pk_cent_key,
            r.cent_name,
            r.fk_student_key,
            r.st_first_name,
            r.st_surname,
            r.first_started_on,
            r.is_trial_at_session ? "Trial" : "Main",
            r.subscription_type_at_session || "",
        ]);
        const label = detailsMonth ? detailsMonth : "details";
        downloadCSV(`lll-usage-${label}.csv`, [header, ...body]);
    };

    return (
        <Container fluid className="mx-0 px-0">
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

            {/* Breadcrumbs */}
            <Row>
                <Col xs="12" className="mb-3">
                    <Breadcrumbs aria-label="breadcrumb">
                        <Typography color="text.primary">Admin</Typography>
                        <Typography color="text.primary" className="fw-bold">
                            Student App
                        </Typography>
                        <Typography color="text.primary">LLL Usage</Typography>
                    </Breadcrumbs>
                    <h4 className="mt-2 mb-0">LLL Usage — Monthly Unique Students</h4>

                </Col>
            </Row>

            <Row>
                <Col xs="12">
                    <Card className="shadow-sm">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <Button variant="secondary" size="sm" onClick={fetchCounts} disabled={loading}>
                                        {loading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Refreshing...
                                            </>
                                        ) : (
                                            "Refresh"
                                        )}
                                    </Button>
                                </div>
                                <div>
                                    <Button variant="outline-primary" size="sm" onClick={onExportMonthly} disabled={!rows.length}>
                                        Export CSV
                                    </Button>
                                </div>
                            </div>

                            <div className="table-responsive">
                                <Table hover bordered className="align-middle mb-0">
                                    <thead className="table-light">
                                    <tr>
                                        <th style={{ width: "220px" }}>Month</th>
                                        <th className="text-end">Main Students</th>
                                        <th className="text-end">Trial Students</th>
                                        <th className="text-end">Total</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {loading && (
                                        <tr>
                                            <td colSpan={4} className="text-center py-4">
                                                <Spinner animation="border" />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && rows.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="text-center text-muted py-4">
                                                No usage data found.
                                            </td>
                                        </tr>
                                    )}
                                    {!loading &&
                                        rows.map((r, idx) => {
                                            const total =
                                                Number(r.unique_students_main || 0) + Number(r.unique_students_trial || 0);
                                            return (
                                                <tr
                                                    key={idx}
                                                    className="cursor-pointer"
                                                    onClick={() => openDetails(r.month)}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <td className="fw-semibold">{formatMonthLabel(r.month)}</td>
                                                    <td className="text-end">{r.unique_students_main}</td>
                                                    <td className="text-end">{r.unique_students_trial}</td>
                                                    <td className="text-end">{total}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    {rows.length > 0 && (
                                        <tfoot>
                                        <tr className="table-light">
                                            <th className="text-end">Totals:</th>
                                            <th className="text-end">{totals.main}</th>
                                            <th className="text-end">{totals.trial}</th>
                                            <th className="text-end">{totals.all}</th>
                                        </tr>
                                        </tfoot>
                                    )}
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Details Modal */}
            <Modal show={showDetails} onHide={() => setShowDetails(false)} size="xl" scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>Students — {detailsMonth ? formatMonthLabel(detailsMonth) : "-"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="mb-3">
                        <Col md={6} className="d-flex gap-2 align-items-center">
                            <Form.Label className="mb-0">Classification:</Form.Label>
                            <Form.Select
                                size="sm"
                                style={{ maxWidth: 220 }}
                                value={which}
                                onChange={(e) => setWhich(e.target.value)}
                            >
                                <option value="all">All</option>
                                <option value="main">Main</option>
                                <option value="trial">Trial</option>
                            </Form.Select>
                            <Button size="sm" variant="secondary" onClick={refreshDetails} disabled={detailsLoading}>
                                {detailsLoading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Loading...
                                    </>
                                ) : (
                                    "Apply"
                                )}
                            </Button>
                        </Col>
                        <Col md={6} className="text-end">
                            <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={onExportDetails}
                                disabled={!detailsRows.length}
                            >
                                Export CSV
                            </Button>
                        </Col>
                    </Row>

                    <div className="table-responsive">
                        <Table hover bordered className="align-middle">
                            <thead className="table-light">
                            <tr>
                                <th>Centre</th>
                                <th>Student</th>
                                <th>First Session</th>
                                <th>Classification</th>
                                <th>Subscription Type @ Session</th>
                            </tr>
                            </thead>
                            <tbody>
                            {detailsLoading && (
                                <tr>
                                    <td colSpan={5} className="text-center py-4">
                                        <Spinner animation="border" />
                                    </td>
                                </tr>
                            )}
                            {!detailsLoading && detailsRows.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center text-muted py-4">
                                        No students found for this month.
                                    </td>
                                </tr>
                            )}
                            {!detailsLoading &&
                                detailsRows.map((r, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <div className="fw-semibold">{r.cent_name}</div>
                                            <div className="text-muted small">{r.pk_cent_key}</div>
                                        </td>
                                        <td>
                                            <div className="fw-semibold">
                                                {r.st_first_name} {r.st_surname}
                                            </div>
                                            <div className="text-muted small">{r.fk_student_key}</div>
                                        </td>
                                        <td>{r.first_started_on}</td>
                                        <td>{r.is_trial_at_session ? "Trial" : "Main"}</td>
                                        <td>{r.subscription_type_at_session || "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetails(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}
